import React from "react";
import { Link } from "react-router-dom";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');

  :root {
    --ink: #0f0e0d;
    --ink-muted: #5a5650;
    --ink-faint: #b8b2aa;
    --paper: #faf8f4;
    --paper-warm: #f2ede4;
    --accent: #c8392b;
    --accent-light: #f5e8e6;
    --rule: #ddd8cf;
    --serif: 'Playfair Display', Georgia, serif;
    --sans: 'DM Sans', system-ui, sans-serif;
  }

  .blog-root * { box-sizing: border-box; margin: 0; padding: 0; }
  .blog-root { font-family: var(--sans); background: var(--paper); color: var(--ink); min-height: 100vh; }

  .blog-header {
    border-bottom: 1px solid var(--rule);
    padding: 14px 0;
    background: var(--paper);
    position: sticky; top: 0; z-index: 50;
  }
  .blog-header-inner {
    max-width: 1200px; margin: 0 auto; padding: 0 32px;
    display: flex; align-items: center; justify-content: space-between;
  }
  .blog-header-pub {
    font-family: var(--serif); font-size: 18px; font-weight: 700; letter-spacing: -0.01em;
  }
  .blog-header-tag {
    font-size: 11px; font-weight: 500; letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--ink-muted); background: var(--paper-warm);
    border: 1px solid var(--rule); border-radius: 2px; padding: 4px 10px;
  }

  .blog-hero { max-width: 1200px; margin: 0 auto; padding: 64px 32px 0; }

  .blog-kicker {
    display: flex; align-items: center; gap: 12px; margin-bottom: 28px;
  }
  .blog-kicker-line { flex: 0 0 32px; height: 2px; background: var(--accent); }
  .blog-kicker-text {
    font-size: 11px; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase;
    color: var(--accent);
  }

  .blog-title {
    font-family: var(--serif); font-size: clamp(36px, 5.5vw, 72px);
    font-weight: 900; line-height: 1.05; letter-spacing: -0.02em;
    color: var(--ink); max-width: 900px;
  }
  .blog-title em { font-style: italic; color: var(--accent); }

  .blog-deck {
    margin-top: 24px; font-size: 18px; font-weight: 300; line-height: 1.65;
    color: var(--ink-muted); max-width: 600px; border-left: 2px solid var(--rule);
    padding-left: 20px;
  }

  .blog-hero-image {
    margin-top: 48px; position: relative; overflow: hidden;
    aspect-ratio: 16 / 7; background: var(--paper-warm);
  }
  .blog-hero-image img {
    width: 100%; height: 100%; object-fit: cover;
    display: block; transition: transform 8s ease;
  }
  .blog-hero-image:hover img { transform: scale(1.03); }
  .blog-hero-caption {
    position: absolute; bottom: 16px; right: 16px;
    font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase;
    color: rgba(255,255,255,0.7); background: rgba(0,0,0,0.4);
    padding: 4px 10px; border-radius: 2px;
  }

  .blog-divider {
    max-width: 1200px; margin: 0 auto; padding: 0 32px;
    display: flex; align-items: center; gap: 24px; margin-top: 56px;
  }
  .blog-divider hr { flex: 1; border: none; border-top: 1px solid var(--rule); }
  .blog-divider-ornament { font-family: var(--serif); font-size: 20px; color: var(--ink-faint); }

  .blog-body {
    max-width: 1200px; margin: 0 auto; padding: 0 32px 96px;
    display: grid; grid-template-columns: 280px 1fr;
    gap: 0 80px; margin-top: 56px;
    align-items: start;
  }

  .blog-sidebar { position: sticky; top: 80px; }

  .blog-sidebar-lead {
    font-family: var(--serif); font-size: 22px; font-weight: 700;
    line-height: 1.35; letter-spacing: -0.01em; color: var(--ink);
    margin-bottom: 24px; padding-bottom: 24px; border-bottom: 1px solid var(--rule);
  }

  .blog-meta-row {
    display: flex; flex-direction: column; gap: 14px; margin-bottom: 32px;
  }
  .blog-meta-item { font-size: 12px; color: var(--ink-muted); }
  .blog-meta-label {
    display: block; font-weight: 500; letter-spacing: 0.1em;
    text-transform: uppercase; font-size: 10px; color: var(--ink-faint); margin-bottom: 3px;
  }

  .blog-toc { margin-top: 32px; }
  .blog-toc-title {
    font-size: 10px; font-weight: 500; letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--ink-faint); margin-bottom: 14px;
  }
  .blog-toc-list { list-style: none; display: flex; flex-direction: column; gap: 8px; }
  .blog-toc-item a {
    font-size: 13px; color: var(--ink-muted); text-decoration: none;
    display: flex; align-items: center; gap: 8px;
    transition: color 0.15s;
  }
  .blog-toc-item a:hover { color: var(--accent); }
  .blog-toc-item a::before {
    content: ''; flex: 0 0 16px; height: 1px; background: currentColor; opacity: 0.4;
  }

  .blog-article { min-width: 0; }

  .blog-article h2 {
    font-family: var(--serif); font-size: 30px; font-weight: 700;
    line-height: 1.2; letter-spacing: -0.01em; color: var(--ink);
    margin-top: 56px; margin-bottom: 20px; padding-top: 56px;
    border-top: 1px solid var(--rule);
  }
  .blog-article h2:first-child { margin-top: 0; padding-top: 0; border-top: none; }

  .blog-article p {
    font-size: 17px; line-height: 1.78; color: #2a2724; margin-bottom: 20px;
  }

  .blog-article strong { font-weight: 600; color: var(--ink); }

  .blog-impact {
    font-family: var(--serif); font-size: 20px; font-style: italic;
    line-height: 1.5; color: var(--ink); background: var(--paper-warm);
    border-left: 3px solid var(--accent); padding: 20px 24px;
    margin: 28px 0;
  }

  .blog-article ul {
    margin: 0 0 20px 0; padding: 0; list-style: none;
    display: flex; flex-direction: column; gap: 8px;
  }
  .blog-article ul li {
    font-size: 16px; line-height: 1.6; color: #2a2724;
    display: flex; align-items: flex-start; gap: 12px;
  }
  .blog-article ul li::before {
    content: '\u2014'; color: var(--accent); font-weight: 700;
    flex-shrink: 0; margin-top: 1px;
  }

  .blog-article blockquote {
    margin: 40px 0; padding: 32px 40px;
    background: var(--ink); color: var(--paper);
    position: relative;
  }
  .blog-article blockquote::before {
    content: '\u201C';
    font-family: var(--serif); font-size: 80px; line-height: 0.8;
    color: var(--accent); position: absolute; top: 24px; left: 24px;
    opacity: 0.6;
  }
  .blog-article blockquote p {
    font-family: var(--serif); font-size: 21px; font-style: italic;
    line-height: 1.5; color: var(--paper); margin: 0; padding-left: 32px;
  }

  /* Before / After comparison */
  .blog-compare {
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 1px; background: var(--rule);
    border: 1px solid var(--rule); margin: 28px 0 36px;
  }
  .blog-compare-col { background: var(--paper); padding: 28px 24px; }
  .blog-compare-col.after { background: var(--ink); }
  .blog-compare-heading {
    font-size: 10px; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase;
    color: var(--accent); margin-bottom: 16px; display: flex; align-items: center; gap: 8px;
  }
  .blog-compare-col.after .blog-compare-heading { color: #e87d72; }
  .blog-compare-col ul {
    margin: 0; padding: 0; list-style: none;
    display: flex; flex-direction: column; gap: 8px;
  }
  .blog-compare-col ul li {
    font-size: 14px; line-height: 1.55; color: var(--ink-muted);
    display: flex; align-items: flex-start; gap: 10px;
  }
  .blog-compare-col.after ul li { color: #c8bfb5; }
  .blog-compare-col ul li::before {
    content: '\u2014'; color: var(--accent); font-weight: 700; flex-shrink: 0;
  }
  .blog-compare-col.after ul li::before { color: #e87d72; }
  .blog-compare-result {
    grid-column: 1 / -1; padding: 16px 24px;
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 1px; background: var(--rule);
  }
  .blog-compare-result-cell {
    padding: 14px 0; background: var(--paper-warm);
    font-size: 13px; color: var(--ink-muted); text-align: center;
  }
  .blog-compare-result-cell.good { background: var(--accent); color: white; font-weight: 500; }

  /* ── Prev / Next Nav ── */
  .blog-nav {
    border-top: 1px solid var(--rule);
    max-width: 1200px; margin: 0 auto;
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 1px; background: var(--rule);
  }
  .blog-nav-cell {
    background: var(--paper); padding: 32px 40px;
    text-decoration: none; display: flex; flex-direction: column; gap: 8px;
    transition: background 0.15s;
  }
  .blog-nav-cell:hover { background: var(--paper-warm); }
  .blog-nav-cell.next { text-align: right; align-items: flex-end; }
  .blog-nav-cell.prev { align-items: flex-start; }
  .blog-nav-label {
    font-size: 10px; font-weight: 500; letter-spacing: 0.14em;
    text-transform: uppercase; color: var(--accent);
    display: flex; align-items: center; gap: 8px;
  }
  .blog-nav-cell.next .blog-nav-label { flex-direction: row-reverse; }
  .blog-nav-label::before {
    content: ''; display: block; width: 20px; height: 1px; background: var(--accent);
  }
  .blog-nav-title {
    font-family: var(--serif); font-size: 18px; font-weight: 700;
    line-height: 1.25; color: var(--ink); max-width: 340px;
  }
  .blog-nav-arrow {
    font-size: 20px; color: var(--accent); margin-top: 4px;
    transition: transform 0.2s ease;
  }
  .blog-nav-cell:hover .blog-nav-arrow { transform: translateX(4px); }
  .blog-nav-cell.prev:hover .blog-nav-arrow { transform: translateX(-4px); }

  .blog-footer {
    border-top: 1px solid var(--rule); background: var(--paper-warm);
    padding: 40px 32px; text-align: center;
  }
  .blog-footer p { font-size: 13px; color: var(--ink-faint); }
  .blog-footer strong { color: var(--ink-muted); }

  @media (max-width: 900px) {
    .blog-body { grid-template-columns: 1fr; gap: 40px; }
    .blog-sidebar { position: static; }
    .blog-toc { display: none; }
    .blog-compare { grid-template-columns: 1fr; }
    .blog-compare-result { grid-template-columns: 1fr; }
    .blog-compare-result-cell { text-align: left; padding: 12px 16px; }
    .blog-hero { padding: 40px 20px 0; }
    .blog-body { padding: 0 20px 64px; margin-top: 40px; }
    .blog-divider { padding: 0 20px; }
  }
`;

export default function BlogPart2() {
  return (
    <>
      <style>{styles}</style>
      <div className="blog-root">

        

        <div className="blog-hero">
          <div className="blog-kicker">
            <span className="blog-kicker-line" />
            <span className="blog-kicker-text">Decision Integrity — Before & After [Part II]</span>
          </div>

          <h1 className="blog-title">
            What Changed When the System <em>Started Enforcing It</em>
          </h1>

          <p className="blog-deck">
            The same charges. The same data. A different layer added between the decision and the invoice — and the results were immediate.
          </p>

          <div className="blog-hero-image">
            <img
              src="https://images.unsplash.com/photo-1518186285589-2f7649de83e0?q=80&w=1600&auto=format&fit=crop"
              alt="System validation and data integrity"
            />
            <span className="blog-hero-caption">Systems · Validation · Results</span>
          </div>
        </div>

        <div className="blog-divider">
          <hr />
          <span className="blog-divider-ornament">§</span>
          <hr />
        </div>

        <div className="blog-body">

          <aside className="blog-sidebar">
            <p className="blog-sidebar-lead">
              No manual review. No extra work. Just a layer that asked: is this decision ready?
            </p>

            <div className="blog-meta-row">
              <div className="blog-meta-item">
                <span className="blog-meta-label">Series</span>
                Decision Integrity
              </div>
              <div className="blog-meta-item">
                <span className="blog-meta-label">Reading Time</span>
                3 min
              </div>
              <div className="blog-meta-item">
                <span className="blog-meta-label">Topic</span>
                Systems · Billing · Outcomes
              </div>
            </div>

            <nav className="blog-toc">
              <p className="blog-toc-title">In this article</p>
              <ul className="blog-toc-list">
                {["Before the change", "The layer added", "After the change", "The result"].map(t => (
                  <li key={t} className="blog-toc-item"><a href="#">{t}</a></li>
                ))}
              </ul>
            </nav>
          </aside>

          <article className="blog-article">

            <h2>The state of things before</h2>
            <p>
              Without a validation layer, the system defaulted to action. Data arrived,
              charges were applied, invoices went out. Speed was the priority.
            </p>

            <div className="blog-compare">
              <div className="blog-compare-col">
                <div className="blog-compare-heading">Before</div>
                <ul>
                  <li>Charges applied immediately on data arrival</li>
                  <li>Timeline inconsistencies ignored</li>
                  <li>Invoices sent "as is" without cross-system validation</li>
                </ul>
              </div>
              <div className="blog-compare-col after">
                <div className="blog-compare-heading">After</div>
                <ul>
                  <li>Timeline validated across all connected systems</li>
                  <li>Weak or inconsistent data blocked before billing</li>
                  <li>Charges only applied when fully defensible</li>
                </ul>
              </div>
              <div className="blog-compare-result">
                <div className="blog-compare-result-cell">Result: disputes, delays, partial payments</div>
                <div className="blog-compare-result-cell good">Result: clean billing, no disputes</div>
              </div>
            </div>

            <h2>The layer added</h2>
            <p>
              A Decision Integrity layer was introduced between data ingestion and invoice generation.
              It did one thing: checked whether the system state was aligned enough to support the decision.
            </p>
            <p className="blog-impact">
              If the data wasn't strong enough — the charge waited. Not cancelled. Not overridden. Just held.
            </p>
            <p>
              No manual review. No extra work for the operations team. The system handled the check automatically.
            </p>

            <blockquote>
              <p>The difference wasn't accuracy. It was timing.</p>
            </blockquote>

            <h2>The result</h2>
            <ul>
              <li>No dispute on the first invoice sent under the new system</li>
              <li>No delay — payment arrived within the standard cycle</li>
              <li>Clean, repeatable billing with no reconciliation required</li>
            </ul>

            <p>
              The charges were the same. The underlying data was the same.
              What changed was the moment at which the decision was made — and that was enough.
            </p>

          </article>
        </div>

        <nav className="blog-nav">
          <Link to="/blogs/1" className="blog-nav-cell prev">
            <span className="blog-nav-label">Previous</span>
            <span className="blog-nav-title">Part I: The Detention Dispute That Looked Valid — Until It Didn't</span>
            <span className="blog-nav-arrow">←</span>
          </Link>
          <Link to="/blogs/3" className="blog-nav-cell next">
            <span className="blog-nav-label">Next</span>
            <span className="blog-nav-title">Part III: What Actually Changes When You Stop Sending Weak Charges</span>
            <span className="blog-nav-arrow">→</span>
          </Link>
        </nav>

        <footer className="blog-footer">
       <p><strong style={{ color: "var(--ink-muted)" }}>Documore LLC</strong> — © {new Date().getFullYear()}</p>
        </footer>

      </div>
    </>
  );
}
